import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import apiClient from '../../../lib/apiClient';

/**
 * Hook for fetching CISA Zero Trust Maturity Model reference data
 *
 * Provides access to:
 * - Pillars (5 total)
 * - Functions (35 total across pillars)
 * - Cross-cutting capabilities (3 total)
 * - Maturity criteria (verbatim from CISA PDF)
 * - Products with configurations
 *
 * Data is cached and shared across components.
 */

// Stale time for reference data (mostly static)
const STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch the complete CISA framework in one call
 * Returns: { pillars, crossCutting, maturityLevels }
 */
export function useCisaFramework() {
  return useQuery({
    queryKey: ['cisa', 'framework'],
    queryFn: () => apiClient.get('/api/cisa/framework'),
    staleTime: STALE_TIME,
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Fetch CISA pillars
 */
export function useCisaPillars() {
  return useQuery({
    queryKey: ['cisa', 'pillars'],
    queryFn: () => apiClient.get('/api/cisa/pillars'),
    staleTime: STALE_TIME,
  });
}

/**
 * Fetch CISA functions, optionally filtered by pillar
 * @param {string} pillarCode - Optional pillar code to filter by
 * @param {boolean} includeCrossCutting - Include cross-cutting functions (default: true)
 */
export function useCisaFunctions(pillarCode, includeCrossCutting = true) {
  const params = new URLSearchParams();
  if (pillarCode) params.set('pillar', pillarCode);
  if (!includeCrossCutting) params.set('includeCrossCutting', 'false');

  const queryString = params.toString();

  return useQuery({
    queryKey: ['cisa', 'functions', pillarCode, includeCrossCutting],
    queryFn: () => apiClient.get(`/api/cisa/functions${queryString ? `?${queryString}` : ''}`),
    staleTime: STALE_TIME,
  });
}

/**
 * Fetch cross-cutting capabilities
 */
export function useCisaCrossCutting() {
  return useQuery({
    queryKey: ['cisa', 'cross-cutting'],
    queryFn: () => apiClient.get('/api/cisa/cross-cutting'),
    staleTime: STALE_TIME,
  });
}

/**
 * Fetch maturity criteria, optionally filtered
 * @param {string} functionCode - Optional function code to filter by
 * @param {string} level - Optional maturity level to filter by
 */
export function useCisaMaturityCriteria(functionCode, level) {
  const params = new URLSearchParams();
  if (functionCode) params.set('function', functionCode);
  if (level) params.set('level', level);

  const queryString = params.toString();

  return useQuery({
    queryKey: ['cisa', 'maturity-criteria', functionCode, level],
    queryFn: () => apiClient.get(`/api/cisa/maturity-criteria${queryString ? `?${queryString}` : ''}`),
    staleTime: STALE_TIME,
  });
}

/**
 * Fetch product catalog, optionally filtered
 * @param {Object} filters - { category, vendor, functionCode }
 */
export function useCisaProducts(filters = {}) {
  const params = new URLSearchParams();
  if (filters.category) params.set('category', filters.category);
  if (filters.vendor) params.set('vendor', filters.vendor);
  if (filters.functionCode) params.set('function', filters.functionCode);

  const queryString = params.toString();

  return useQuery({
    queryKey: ['cisa', 'products', filters],
    queryFn: () => apiClient.get(`/api/cisa/products${queryString ? `?${queryString}` : ''}`),
    staleTime: STALE_TIME,
  });
}

/**
 * Fetch products with their configurations in one call
 * Useful for ProductAssessmentPanel
 * @param {string} functionCode - Optional function code to filter products by
 */
export function useCisaProductsWithConfigs(functionCode) {
  const params = functionCode ? `?function=${functionCode}` : '';

  return useQuery({
    queryKey: ['cisa', 'products-with-configs', functionCode],
    queryFn: () => apiClient.get(`/api/cisa/products-with-configs${params}`),
    staleTime: STALE_TIME,
  });
}

/**
 * Fetch configurations for a specific product
 * @param {number} productId - Product ID
 */
export function useCisaProductConfigurations(productId) {
  return useQuery({
    queryKey: ['cisa', 'product-configurations', productId],
    queryFn: () => apiClient.get(`/api/cisa/products/${productId}/configurations`),
    staleTime: STALE_TIME,
    enabled: !!productId,
  });
}

// ============================================================================
// DERIVED DATA HELPERS
// ============================================================================

/**
 * Transform framework data into the format expected by existing components
 * This provides backwards compatibility with the JS config file format
 */
export function useCisaFrameworkCompat() {
  const { data: framework, isLoading, error } = useCisaFramework();

  const compatData = useMemo(() => {
    if (!framework) return null;

    // Transform pillars to match CISA_PILLARS format
    const CISA_PILLARS = framework.pillars.map(p => ({
      code: p.pillar_code,
      name: p.name,
      description: p.description,
      color: p.color,
      icon: p.icon
    }));

    // Transform functions to match CISA_FUNCTIONS format (grouped by pillar)
    const CISA_FUNCTIONS = {};
    const CISA_CORE_FUNCTIONS = {};

    framework.pillars.forEach(pillar => {
      CISA_FUNCTIONS[pillar.pillar_code] = pillar.functions.map(f => ({
        code: f.function_code,
        name: f.name,
        description: f.description,
        order: f.display_order,
        isCrossCutting: f.is_cross_cutting,
        isNew: f.is_new_in_v2
      }));

      CISA_CORE_FUNCTIONS[pillar.pillar_code] = pillar.functions
        .filter(f => !f.is_cross_cutting)
        .map(f => ({
          code: f.function_code,
          name: f.name,
          description: f.description,
          order: f.display_order,
          isNew: f.is_new_in_v2
        }));
    });

    // Transform cross-cutting to match CROSS_CUTTING format
    const CROSS_CUTTING = framework.crossCutting.map(cc => ({
      code: cc.capability_code,
      name: cc.name,
      description: cc.description
    }));

    // Transform maturity criteria to match MATURITY_CRITERIA format
    const MATURITY_CRITERIA = {};

    framework.pillars.forEach(pillar => {
      pillar.functions.forEach(fn => {
        if (fn.criteria && Object.keys(fn.criteria).length > 0) {
          MATURITY_CRITERIA[fn.function_code] = {};
          Object.entries(fn.criteria).forEach(([level, data]) => {
            MATURITY_CRITERIA[fn.function_code][level] = data.text;
          });
        }
      });
    });

    framework.crossCutting.forEach(cc => {
      if (cc.criteria && Object.keys(cc.criteria).length > 0) {
        MATURITY_CRITERIA[cc.capability_code] = {};
        Object.entries(cc.criteria).forEach(([level, data]) => {
          MATURITY_CRITERIA[cc.capability_code][level] = data.text;
        });
      }
    });

    return {
      CISA_PILLARS,
      CISA_FUNCTIONS,
      CISA_CORE_FUNCTIONS,
      CROSS_CUTTING,
      MATURITY_CRITERIA,
      MATURITY_LEVELS: framework.maturityLevels
    };
  }, [framework]);

  return {
    data: compatData,
    isLoading,
    error,
    // Also expose raw framework for advanced use
    rawFramework: framework
  };
}

/**
 * Transform products with configs into the format expected by ProductAssessmentPanel
 */
export function useCisaProductsCompat(functionCode) {
  const { data: products, isLoading, error } = useCisaProductsWithConfigs(functionCode);

  const compatData = useMemo(() => {
    if (!products) return null;

    // Transform to match PRODUCT_MATURITY_MAPPINGS format
    const PRODUCT_MATURITY_MAPPINGS = {};

    products.forEach(product => {
      PRODUCT_MATURITY_MAPPINGS[product.product_code] = {
        id: product.product_code,
        name: product.name,
        vendor: product.vendor,
        category: product.category,
        icon: product.icon,
        description: product.description,
        contributesTo: product.contributes_to,
        configurations: product.configurations.map(config => {
          // Build maturity mapping object from array
          const maturityMapping = {};
          if (config.maturity_mappings) {
            config.maturity_mappings.forEach(m => {
              maturityMapping[m.function_code] = m.maturity_level;
            });
          }

          return {
            id: config.config_code,
            label: config.label,
            shortLabel: config.short_label,
            icon: config.icon,
            description: config.description,
            maturityMapping,
            criteria: config.criteria,
            upgradeHint: config.upgrade_hint
          };
        })
      };
    });

    return PRODUCT_MATURITY_MAPPINGS;
  }, [products]);

  // Helper functions matching productMaturityMappings.js exports
  const getProductsForFunction = useMemo(() => {
    if (!compatData) return () => [];
    return (fnCode) => {
      return Object.values(compatData).filter(
        product => product.contributesTo.includes(fnCode)
      );
    };
  }, [compatData]);

  const getProductById = useMemo(() => {
    if (!compatData) return () => null;
    return (productId) => compatData[productId] || null;
  }, [compatData]);

  const getAllProducts = useMemo(() => {
    if (!compatData) return () => [];
    return () => Object.values(compatData);
  }, [compatData]);

  // Get upgrade recommendation for a product configuration
  const getUpgradeRecommendation = useMemo(() => {
    if (!compatData) return () => null;
    return (productId, currentConfigId) => {
      const product = compatData[productId];
      if (!product) return null;

      const currentIndex = product.configurations.findIndex(c => c.id === currentConfigId);
      if (currentIndex === -1 || currentIndex >= product.configurations.length - 1) {
        return null; // Already at highest or not found
      }

      const currentConfig = product.configurations[currentIndex];
      const nextConfig = product.configurations[currentIndex + 1];

      return {
        currentConfig,
        nextConfig,
        upgradeHint: currentConfig.upgradeHint
      };
    };
  }, [compatData]);

  return {
    data: compatData,
    isLoading,
    error,
    // Helper functions
    getProductsForFunction,
    getProductById,
    getAllProducts,
    getUpgradeRecommendation,
    // Raw data
    rawProducts: products
  };
}

/**
 * Prefetch CISA reference data
 * Call this early to warm the cache
 */
export function usePrefetchCisaData() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.prefetchQuery({
      queryKey: ['cisa', 'framework'],
      queryFn: () => apiClient.get('/api/cisa/framework'),
      staleTime: STALE_TIME,
    });

    queryClient.prefetchQuery({
      queryKey: ['cisa', 'products-with-configs', undefined],
      queryFn: () => apiClient.get('/api/cisa/products-with-configs'),
      staleTime: STALE_TIME,
    });
  };
}

export default useCisaFrameworkCompat;
